/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useContext, useState } from 'react';
import { Dimmer, Form, Placeholder } from 'semantic-ui-react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom-v5-compat';
import { Table, Label, Segment, Button, Pagination, Checkbox, Input, Dropdown, Icon } from 'semantic-ui-react';
import { EditableBarcodes } from './EditableBarcodes/EditableBarcodes';
import DisplayMecanism from './DisplayMecanism/DisplayMecanism';
import { EditablePromotionStatus } from './EditablePromotionStatus/EditablePromotionStatus';
import { useTranslation } from 'react-i18next';
import { UltyModalWrapperContext } from '../../Shared/UltyModalWrapper/UltyModalWrapperContext';
import { useBulkUpdateCampaignPromotionsMutation, useCampaignsReload, useUpdateCampaignStatusMutation } from '../_hooks/useCampaigns';


const BulkPromotionActionConfirm = ({ count, actionLabel, confirmLabel, onCancel, onConfirm, isPending }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ margin: 0 }}>
        {actionLabel}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button basic onClick={onCancel} disabled={isPending}>
          <Icon name="remove" />
          Annuler
        </Button>
        <Button color="teal" onClick={onConfirm} loading={isPending}>
          <Icon name="checkmark" />
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}


const SourceText = ({source,externalId}) => {
  const text = [source,externalId].filter(Boolean)
  if(text.length === 0) return <span>-</span>;
  return <span>{text.join(' | ')}</span> ;
}



const PromotionCampaignDetail = ({ campaign, isLoading }) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // You can adjust this as needed
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { handleUltyModalWrapper } = useContext(UltyModalWrapperContext);
  const reloadCampaigns = useCampaignsReload();

  const { currentFilters } = useOutletContext();
  const { status } = campaign || {};

  // Filter state
  const [hideDisabled, setHideDisabled] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');

  const updatePromotionCampaignMutation = useUpdateCampaignStatusMutation();

  const isEditable = campaign && !isLoading && status !== 'COMPLETED';

  // Filtering logic
  let promotions = campaign?.promotions || [];
  // Count DRAFT promotions (before filtering)
  const draftPromotions = (promotions || []).filter((promo) => promo.status === 'DRAFT')
  const remainingDraftPromotionsCount = draftPromotions.length;
  if (hideDisabled) {
    promotions = promotions.filter((promo) => promo.status !== 'DISABLED');
  }
  if (searchFilter.trim() !== '') {
    const filterValue = searchFilter.trim().toLowerCase();
    promotions = promotions.filter((promo) =>
      (promo.externalId || '').toLowerCase().includes(filterValue) ||
      (promo.barcodes || []).some((b) => b.toLowerCase().includes(filterValue))
    );
  }

  const bulkPromotionActionMutation = useBulkUpdateCampaignPromotionsMutation(campaign?.id, {
    onSuccess: () => {
      setHideDisabled(true);
    },
  });

  const handleBulkPromotionAction = async (status) => {
    if (remainingDraftPromotionsCount === 0) {
      return;
    }

    const isEnableAction = status === 'ENABLED';
    const actionLabel = isEnableAction
      ? t(
        'promotion_campaign.promotions.confirm_enable_action',
        'Confirmer l\'activation des {{count}} promotions restantes ?',
        { count: remainingDraftPromotionsCount }
      )
      : t(
        'promotion_campaign.promotions.confirm_disable_action',
        'Confirmer la désactivation des {{count}} promotions restantes ?',
        { count: remainingDraftPromotionsCount }
      );

    const confirmLabel = isEnableAction
      ? t('promotion_campaign.promotions.enable_remaining', 'Activer les {{count}} promotions restantes', { count: remainingDraftPromotionsCount })
      : t('promotion_campaign.promotions.disable_remaining', 'Désactiver les {{count}} promotions restantes', { count: remainingDraftPromotionsCount });

    handleUltyModalWrapper(true, {
      title: isEnableAction
        ? t('promotion_campaign.promotions.confirm_enable_title', 'Confirmer l\'activation')
        : t('promotion_campaign.promotions.confirm_disable_title', 'Confirmer la désactivation'),
      closeIcon: true,
      size: 'small',
      component: (
        <BulkPromotionActionConfirm
          count={remainingDraftPromotionsCount}
          actionLabel={actionLabel}
          confirmLabel={confirmLabel}
          isPending={bulkPromotionActionMutation.isPending}
          onCancel={() => handleUltyModalWrapper(false, null)}
          onConfirm={() => {
            bulkPromotionActionMutation.mutate({ ids: draftPromotions.map((promo) => promo.id), status });
            handleUltyModalWrapper(false, null);
          }}
        />
      ),
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(promotions.length / pageSize);
  const paginatedPromotions = promotions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <Segment isLoading={isLoading} style={{ height: 'calc(100% - 100px)', display: 'flex', flexDirection: 'column', minHeight: 500 }}>
      <div style={{ flex: 'none' }}>
        <Button
          basic
          size="tiny"
          icon="arrow left"
          content={t('promotion_campaign.back_to_list', 'Revenir à la liste')}
          onClick={() => navigate(`/categories/promotion-campaigns${location.search}`)}
          style={{ marginBottom: 16 }}
        />
        <br />
        {isEditable && (
          <Button.Group size="large">
            {status !== 'ENABLED' && (
              <Button
                loading={updatePromotionCampaignMutation.isPending}
                color="teal"
                onClick={() => updatePromotionCampaignMutation.mutate({ id: campaign.id, status: 'ENABLED' })}
              >
                {t('promotion_campaign.enable', 'Activer la campagne')}
              </Button>
            )}
            {status !== 'DISABLED' && (
              <Button
                loading={updatePromotionCampaignMutation.isPending}
                color="grey"
                basic
                onClick={() => updatePromotionCampaignMutation.mutate({ id: campaign.id, status: 'DISABLED' })}
              >
                {t('promotion_campaign.disable', 'Désactiver la campagne')}
              </Button>
            )}
          </Button.Group>
        )}

        <h3>{t('promotion_campaign.promotions.header', 'Promotions')}</h3>

        {/* Filter controls */}
         <Form>
            <Form.Group  style={{
      display: 'flex',
      alignItems: 'center', // vertical alignment
      gap: '12px',          // spacing between elements
    }}>
              <Form.Field width={6}>
          <Checkbox
            toggle
            checked={hideDisabled}
            onChange={() => setHideDisabled((v) => !v)}
            label={!hideDisabled ? t('promotion_campaign.promotions.hide_disabled', 'Masquer les promotions désactivées') : t('promotion_campaign.promotions.show_disabled', 'Afficher les promotions désactivées')}
            style={{ marginRight: 8 }}
          />
          </Form.Field>
          <Form.Field  width={5}>
            <Input
              type="text"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t('promotion_campaign.promotions.search_filter_label', 'Filtrer par EAN, ID externe...')}
              style={{ marginLeft: 8, padding: 4 }}
            />
          </Form.Field>
          <Form.Field>
          {remainingDraftPromotionsCount > 0 && (
            <Dropdown
                    text={t(
                    'promotion_campaign.promotions.bulk_actions',
                    'Actions ({{count}} Brouillons)',
                    { count: remainingDraftPromotionsCount }
                  )}
                    floating
                    button
                    direction='left'
                    basic
                    color="blue"
                    size="tiny"
                    loading={bulkPromotionActionMutation.isPending}
                    className="icon teal right labeled">
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleBulkPromotionAction('ENABLED')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Icon color="teal" name="play" />
                    <span>
                      {t(
                        'promotion_campaign.promotions.enable_remaining',
                        'Activer les {{count}} promotions restantes',
                        { count: remainingDraftPromotionsCount }
                      )}
                    </span>
                  </span>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleBulkPromotionAction('DISABLED')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Icon color="orange" name="pause" />
                    <span>
                      {t(
                        'promotion_campaign.promotions.disable_remaining',
                        'Désactiver les {{count}} promotions restantes',
                        { count: remainingDraftPromotionsCount }
                      )}
                    </span>
                  </span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Form.Field>
        </Form.Group>
        </Form>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', marginBottom: 16 }}>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('promotion_campaign.promotions.source', 'Source')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.status', 'Status')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.tags', 'Tags')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.barcodes', 'Code barres')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.mechanism', 'Mécanique')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
          {isLoading && (
            <>
         <Table.Row key="pc-details-table-loading">
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>
                </Table.Cell>
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>                </Table.Cell>
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>
                </Table.Cell>
                <Table.Cell>
                <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>                </Table.Cell>
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>
                </Table.Cell>
              </Table.Row>
        </>
          )}
            {!isLoading && paginatedPromotions.map((promo) => (
              <Table.Row key={promo.id}>
                <Table.Cell>
                  <SourceText source={promo.source} externalId={promo.externalId} />
                </Table.Cell>
                <Table.Cell>
                  <EditablePromotionStatus onEdit={reloadCampaigns} readonly={!isEditable} promotion={promo} />
                </Table.Cell>
                <Table.Cell>{promo.tags?.join(', ')}</Table.Cell>
                <Table.Cell>
                  <EditableBarcodes onEdit={reloadCampaigns} readonly={!isEditable} promotion={promo} currentSearch={currentFilters?.search} />
                </Table.Cell>
                <Table.Cell>{promo.mecanism ? <DisplayMecanism mecanism={promo.mecanism} /> : '-'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        {/* Pagination */}
        {promotions.length > pageSize && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={(_, data) => setCurrentPage(data.activePage)}
              siblingRange={1}
              boundaryRange={1}
            />
          </div>
        )}
	  </div>
  </Segment>
 );
};

export default PromotionCampaignDetail;
